import { useMutation, useQuery, useQueryClient } from "react-query";
import { api } from "~api";
import {
  SellerAdminChatContact,
  SellerAdminChatInboxResponse,
  SellerAdminChatMessage,
  SellerAdminChatMessagesResponse,
  SellerAdminChatTimelineItem,
} from "../types";
import { mergeChatTimeline } from "../components/utils";

const API_BASE = "/admin/seller-chat";

export const sellerAdminChatKeys = {
  all: ["seller-admin-chat"] as const,
  inbox: () => [...sellerAdminChatKeys.all, "inbox"] as const,
  messages: (sellerId: number) =>
    [...sellerAdminChatKeys.all, "messages", sellerId] as const,
};

export function useSellerAdminChatInbox() {
  return useQuery<SellerAdminChatInboxResponse, Error>(
    sellerAdminChatKeys.inbox(),
    async () => {
      const { data } = await api.get<SellerAdminChatInboxResponse>(
        `${API_BASE}/inbox`,
      );
      if (!data.success) {
        throw new Error("Erro ao carregar conversas");
      }
      return data;
    },
    {
      staleTime: 0,
      refetchInterval: 15000,
      refetchOnWindowFocus: true,
    },
  );
}

export function useSellerAdminChatMessages(sellerId?: number) {
  return useQuery<SellerAdminChatTimelineItem[], Error>(
    sellerAdminChatKeys.messages(sellerId ?? 0),
    async () => {
      const { data } = await api.get<SellerAdminChatMessagesResponse>(
        `${API_BASE}/seller/${sellerId}/messages`,
      );
      if (!data.success) {
        throw new Error("Erro ao carregar mensagens");
      }
      return mergeChatTimeline(data.data.messages, data.data.voice_calls);
    },
    {
      enabled: Boolean(sellerId),
      staleTime: 0,
      refetchInterval: 5000,
      refetchOnWindowFocus: true,
    },
  );
}

export function useMarkSellerAdminChatRead() {
  const queryClient = useQueryClient();

  return useMutation(async (sellerId: number) => {
    await api.post(`${API_BASE}/seller/${sellerId}/read`);
  }, {
    onSuccess: () => {
      queryClient.invalidateQueries(sellerAdminChatKeys.inbox());
    },
  });
}

type SendPayload = {
  sellerId: number;
  body: string;
  files: File[];
};

export function useSendSellerAdminChatMessage() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ sellerId, body, files }: SendPayload) => {
      const formData = new FormData();
      if (body.trim()) formData.append("body", body.trim());
      files.forEach((file) => formData.append("files[]", file));

      const { data } = await api.post(
        `${API_BASE}/seller/${sellerId}/messages`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      return data.data.message as SellerAdminChatMessage;
    },
    {
      onSuccess: (_message, variables) => {
        queryClient.invalidateQueries(
          sellerAdminChatKeys.messages(variables.sellerId),
        );
        queryClient.invalidateQueries(sellerAdminChatKeys.inbox());
      },
    },
  );
}

export function findSellerContact(
  contacts: SellerAdminChatContact[],
  sellerId: number,
) {
  return contacts.find((contact) => contact.id === sellerId);
}
